import type { Status } from "@repo/database";
import type {
  ProductCategorySchema,
  ProductCategorySchemaWithId,
} from "@repo/validators";
import type { z } from "zod";
import { useCallback, useEffect } from "react";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";

import type { ProductCategory } from "~/providers/product-provider";
import type { ManagerResult } from "~/types";
import { api } from "~/lib/api";
import { useProductCategoryStore } from "~/stores/use-product-category-store";
import { usePermissionManager } from "./use-permission-manager";

export const useProductCategoryManager = () => {
  const utils = api.useUtils();

  const { hasPermission } = usePermissionManager();

  const { handleProductCategoryUpdate } = useProductCategoryUpdates();

  const { t } = useTranslation("common");

  const {
    setProductCategoryFormOpen,
    setCurrentProductCategory,
    setIsLoading,
    isLoading,
  } = useProductCategoryStore(
    useShallow((state) => ({
      setProductCategoryFormOpen: state.setProductCategoryFormOpen,
      setCurrentProductCategory: state.setCurrentProductCategory,
      setIsLoading: state.setIsLoading,
      isLoading: state.isLoading,
    })),
  );

  const {
    mutate: createProductCategory,
    isPending: isCreatingProductCategory,
  } = api.productCategory.createProductCategory.useMutation({
    async onSuccess(result) {
      toast.success(
        capitalize(t("create_item_success", { item: t("product_category") })),
      );
      await handleProductCategoryUpdate(result);
    },
    onError() {
      toast.error(
        capitalize(t("create_item_error", { item: t("product_category") })),
      );
    },
  });

  const {
    mutate: updateProductCategory,
    isPending: isUpdatingProductCategory,
  } = api.productCategory.updateProductCategory.useMutation({
    async onSuccess(_, variables) {
      toast.success(
        capitalize(
          t("update_item_success", {
            item: t("ProductCategory.product_category"),
          }),
        ),
      );
      await handleProductCategoryUpdate(variables.id);
    },
    onError() {
      toast.error(
        capitalize(t("update_item_error", { item: t("product_category") })),
      );
    },
  });

  const {
    mutate: updateProductCategoryStatus,
    isPending: isUpdatingProductCategoryStatus,
  } = api.productCategory.updateProductCategoryStatus.useMutation({
    async onMutate(updatedProductCategory) {
      await utils.productCategory.getAll.cancel();

      const prevData = utils.productCategory.getAll.getData();

      utils.productCategory.getAll.setData(undefined, (old) => {
        if (!old) return prevData;

        const productCategoryToUpdate = old.find(
          (productCategory) => productCategory.id === updatedProductCategory.id,
        );

        if (!productCategoryToUpdate) {
          return [...old];
        }

        return [
          ...old.filter(
            (productCategory) =>
              productCategory.id !== productCategoryToUpdate.id,
          ),
          {
            ...productCategoryToUpdate,
            status: updatedProductCategory.status,
          },
        ];
      });

      return { prevData };
    },

    onError(_, updatedProductCategory, context) {
      // Rollback to the previous state in case of an error
      utils.productCategory.getAll.setData(undefined, context?.prevData);

      toast.error(
        capitalize(t("update_item_error", { item: t("product_category") })),
        {
          duration: 5000,
        },
      );
    },
  });

  const createProductCategoryHandler = async (
    productCategory: z.infer<ReturnType<typeof ProductCategorySchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_product_category,
      label: t("Permissions.create_product_category_label"),
    });

    if (!permission) return null;

    return await new Promise<boolean>((resolve) => {
      createProductCategory(productCategory, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const updateProductCategoryHandler = async (
    productCategory: z.infer<typeof ProductCategorySchemaWithId>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_product_category,
      label: t("Permissions.update_product_category_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof ProductCategorySchema>>>
    >((resolve) => {
      if (!permission)
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });
      updateProductCategory(productCategory, {
        onSuccess() {
          resolve({
            data: true,
          });
        },
        onError() {
          resolve({
            error: {
              path: "root",
              message: capitalize(
                t("update_item_error", {
                  item: t("ProductCategory.product_category"),
                }),
              ),
            },
          });
        },
      });
    });
  };

  const updateProductCategoryStatusHandler = async (
    id: string,
    status: Status,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_product_category,
      label: t("Permissions.update_product_category_label"),
    });

    if (!permission) return null;

    return new Promise<boolean>((resolve) => {
      updateProductCategoryStatus(
        {
          id,
          status,
        },
        {
          onSuccess() {
            resolve(true);
          },
          onError() {
            resolve(false);
          },
        },
      );
    });
  };

  const openProductCategoryDialog = async (
    productCategory?: ProductCategory,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_product_category,
      label: t("Permissions.create_product_category_label"),
    });

    if (!permission) return null;

    setProductCategoryFormOpen(true);
    setCurrentProductCategory(productCategory);
  };

  useEffect(() => {
    setIsLoading(
      isCreatingProductCategory ||
        isUpdatingProductCategory ||
        isUpdatingProductCategoryStatus,
    );
  }, [
    isCreatingProductCategory,
    isUpdatingProductCategory,
    isUpdatingProductCategoryStatus,
    setIsLoading,
  ]);

  return {
    isLoading,
    openProductCategoryDialog,
    createProductCategory: createProductCategoryHandler,
    updateProductCategory: updateProductCategoryHandler,
    updateProductCategoryStatus: updateProductCategoryStatusHandler,
  };
};

export const useProductCategoryUpdates = () => {
  const utils = api.useUtils();

  const handleProductCategoryUpdate = useCallback(
    async (productCategoryId: string) => {
      const result =
        await utils.productCategory.getById.fetch(productCategoryId);
      // TODO: fix this
      await utils.productModifierGroup.getAll.invalidate();

      void utils.productCategory.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });
    },
    [
      utils.productCategory.getAll,
      utils.productCategory.getById,
      utils.productModifierGroup.getAll,
    ],
  );

  const handleProductCategoryDelete = useCallback(
    (productCategoryId: string) => {
      void utils.productCategory.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== productCategoryId);
      });
    },
    [utils.productCategory.getAll],
  );

  return {
    handleProductCategoryUpdate,
    handleProductCategoryDelete,
  };
};
